import { Injectable } from '@angular/core';
import { ApiBaseService } from './api-base.service';
import { ConfigScheduleItem } from '../pages/config/project-config/config-schedule/config-schedule.types';

const SCHEDULE_CACHE_TTL_MS = 5 * 60 * 1000;

@Injectable({
  providedIn: 'root',
})
export class ScheduleService {
  private readonly scheduleCache = new Map<number, { data: unknown; expiresAt: number }>();
  private readonly batchScheduleCache = new Map<string, { data: unknown; expiresAt: number }>();

  constructor(private apiService: ApiBaseService) {}

  invalidateScheduleCache(projectId?: number): void {
    this.batchScheduleCache.clear();
    if (projectId !== undefined) {
      this.scheduleCache.delete(projectId);
    } else {
      this.scheduleCache.clear();
    }
  }

  async updateScheduleItem(payload: { scheduleItem: ConfigScheduleItem, areaTypeId: number }): Promise<any> {
    return this.apiService.httpRequest('ScheduleService/UpdateScheduleItem', payload, true, true);
  }

  async getScheduleItemConfig(areaTypeId: number): Promise<any> {
    return this.apiService.httpGETRequest('ScheduleService/GetScheduleItemConfig', { areaTypeId }, true);
  }

  async getProjectSchedule(projectId: number, showLoader = true): Promise<any> {
    const entry = this.scheduleCache.get(projectId);
    if (entry && Date.now() < entry.expiresAt) {
      return entry.data;
    }
    const data = await this.apiService.httpGETRequest('ScheduleService/GetProjectSchedule', { projectId }, showLoader);
    if (!data?.hasError) {
      this.scheduleCache.set(projectId, { data, expiresAt: Date.now() + SCHEDULE_CACHE_TTL_MS });
    }
    return data;
  }

  async getProjectSchedules(projectIds: number[], showLoader = true): Promise<any> {
    const key = [...projectIds].sort((a, b) => a - b).join(',');
    const entry = this.batchScheduleCache.get(key);
    if (entry && Date.now() < entry.expiresAt) {
      return entry.data;
    }
    const data = await this.apiService.httpRequest('ScheduleService/GetProjectSchedules', { projectIds }, true, showLoader);
    // A per-project failure comes back in failedProjectIds with hasError false; caching it would
    // pin that project's empty schedule for the full TTL instead of retrying on the next load.
    if (!data?.hasError && !data?.failedProjectIds?.length) {
      this.batchScheduleCache.set(key, { data, expiresAt: Date.now() + SCHEDULE_CACHE_TTL_MS });
    }
    return data;
  }

  async writeProjectScheduleItem(payload: any): Promise<any> {
    return this.apiService.httpRequest('ScheduleService/WriteProjectScheduleItem', payload, true, true);
  }

  async writeProjectScheduleItems(payload: any): Promise<any> {
    return this.apiService.httpRequest('ScheduleService/WriteProjectScheduleItems', payload, true, true);
  }
}